import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ROUTES } from '../common/constants/urls';
import { getProjectSummary, getBudgetandLineItems, retrieveProjectDraws } from '../components/project/project.api';
import { setProjectDrawVisibility } from '../components/project/project-draws/project-draws.api';
import {
  clearRunThisMonthPending,
  createMagicReport,
  getMagicReport,
  resetMagicReport,
  updateHiddenCoLienDrawIds,
} from '../components/monitoring-reports/magic-report/magic-report.api';
import { usePrompt } from '../components/AppPrompt';
import { handleError } from '../common/utils/util.errors';
import { DrawContext } from '../components/monitoring-reports/magic-report/magic-report.type';
import { DrawOption, drawSelectionReplacesReport } from '../components/project/MagicReportDrawSelectDialog';
import {
  getMissingRequiredFields,
  isMagicReportAwaitingPayapp,
  NO_PAYAPPS_UPLOADED_TOOLTIP,
  RequiredFieldGap,
} from '../components/project/project.util';
import { ProjectSummary } from '../components/project/project.type';

interface MagicReportProjectInfo {
  id: string;
  projectNumber: string;
  projectName: string;
}

/**
 * What the user is doing, which decides what confirming the dialog means.
 *
 * - `open` — continue working on the current period's report. Keeping the same draw set just
 *   reopens it; changing the set replaces the report.
 * - `next-report` — the period has rolled over. Resets over every visible draw after a confirm,
 *   without the dialog and without opening the report.
 */
export type MagicReportDrawSelectionMode = 'open' | 'next-report';

/**
 * Owns the "open magic report" and "start next report" flows for every entry point (project
 * footer, project table row).
 */
export function useMagicReportDrawSelection() {
  const navigate = useNavigate();
  const { getConfirmation } = usePrompt();
  const [isPreparing, setIsPreparing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draws, setDraws] = useState<DrawOption[]>([]);
  const [project, setProject] = useState<MagicReportProjectInfo | null>(null);
  const [existingDrawIds, setExistingDrawIds] = useState<string[]>([]);
  const [existingHiddenCoLienDrawIds, setExistingHiddenCoLienDrawIds] = useState<string[]>([]);
  const [existingReportId, setExistingReportId] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<MagicReportDrawSelectionMode>('open');

  // Project title, number, observer, and bank name can never legitimately be missing from a
  // finished report.
  const [requiredFieldsGap, setRequiredFieldsGap] = useState<RequiredFieldGap[]>([]);
  const [requiredFieldsGapProjectId, setRequiredFieldsGapProjectId] = useState<string | null>(null);
  const [requiredFieldsGapProjectSummary, setRequiredFieldsGapProjectSummary] = useState<ProjectSummary | null>(null);
  const [pendingRetry, setPendingRetry] = useState<(() => void) | null>(null);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const closeRequiredFieldsGap = useCallback(() => {
    setRequiredFieldsGap([]);
    setRequiredFieldsGapProjectId(null);
    setRequiredFieldsGapProjectSummary(null);
    setPendingRetry(null);
  }, []);

  /** Loads the project's draws and the existing report's draw set, then opens the dialog. */
  const openDrawSelection = useCallback(
    async (
      projectId: string,
      selectionMode: MagicReportDrawSelectionMode = 'open',
      options?: { onDrawsHidden?: (drawIds: string[]) => void; onNextReportStarted?: (reportId: string) => void },
    ) => {
      if (!projectId) return;
      setIsPreparing(true);
      try {
        const [drawsResponse, projectSummary] = await Promise.all([retrieveProjectDraws(projectId), getProjectSummary(projectId)]);

        if (isMagicReportAwaitingPayapp(projectSummary)) {
          toast.info(`${NO_PAYAPPS_UPLOADED_TOOLTIP}. Upload a payapp first.`);
          return;
        }

        const requiredGap = getMissingRequiredFields(projectSummary);
        if (requiredGap.length > 0) {
          setRequiredFieldsGap(requiredGap);
          setRequiredFieldsGapProjectId(projectId);
          setRequiredFieldsGapProjectSummary(projectSummary);
          setPendingRetry(() => () => openDrawSelection(projectId, selectionMode, options));
          return;
        }

        let available = (((drawsResponse as any)?.draws || []) as DrawOption[]).filter(d => d.isEnabled !== false);
        if (available.length === 0) {
          toast.error('Draw does not exist for this project.');
          return;
        }

        const uploadedDrawIds = projectSummary?.magicReportPayappUploadedDrawIds;
        if (selectionMode === 'open' && Array.isArray(uploadedDrawIds) && available.length > 1) {
          const missing = available.filter(d => !uploadedDrawIds.includes(d.id));
          if (missing.length === available.length) {
            toast.info(`${NO_PAYAPPS_UPLOADED_TOOLTIP}. Upload a payapp first.`);
            return;
          }
          if (missing.length > 0) {
            const confirmed = await getConfirmation({
              heading: 'Payapps not uploaded',
              description: `You have not uploaded a payapp for ${missing.map(d => d.name).join(', ')}. If you proceed, those G702/3s will be hidden, and you cannot go back and upload another payapp.`,
              confirmButton: { label: 'Continue', color: 'primary' },
              cancelButton: { label: 'Cancel', color: 'secondary' },
            });
            if (!confirmed) return;
            const missingIds = missing.map(d => d.id);
            await Promise.all(missingIds.map(id => setProjectDrawVisibility(projectId, id, false)));
            options?.onDrawsHidden?.(missingIds);
            available = available.filter(d => !missingIds.includes(d.id));
          }
        }

        const magicReportId = projectSummary?.magicReportId;
        if (selectionMode === 'next-report' && magicReportId) {
          const confirmed = await getConfirmation({
            heading: 'Start next report',
            description: 'The current report will be archived and its comments will move to “last month”. Continue?',
            confirmButton: { label: 'Continue', color: 'primary' },
            cancelButton: { label: 'Cancel', color: 'secondary' },
          });
          if (!confirmed) return;
          const projectName = (drawsResponse as any)?.projectTitle ?? projectSummary?.projectTitle ?? '';
          const reportName = `Magic Report: ${projectName} - ${available[available.length - 1].name}`;
          const report = await resetMagicReport(
            magicReportId,
            projectSummary.projectId,
            reportName,
            available.map(d => d.id),
            undefined,
            true,
          );
          if (!report?.id) {
            toast.error('Failed to start the next report.');
            return;
          }
          toast.success('Next report started. Upload a payapp to continue.');
          options?.onNextReportStarted?.(report.id);
          return;
        }

        let reportDrawIds: string[] = [];
        let reportHiddenCoLienDrawIds: string[] = [];
        if (magicReportId) {
          try {
            const report = await getMagicReport(magicReportId);
            reportDrawIds = ((report?.drawIds || []) as string[]).filter(id => available.some(d => d.id === id));
            reportHiddenCoLienDrawIds = ((report?.hiddenCoLienDrawIds || []) as string[]).filter(id => reportDrawIds.includes(id));
          } catch {
            // A missing or unreadable report just means nothing to pre-check.
            reportDrawIds = [];
            reportHiddenCoLienDrawIds = [];
          }
        }

        setProject({
          id: projectId,
          projectNumber: (drawsResponse as any)?.projectNumber ?? projectSummary?.projectNumber ?? '',
          projectName: (drawsResponse as any)?.projectTitle ?? projectSummary?.projectTitle ?? '',
        });
        setDraws(available);
        setExistingDrawIds(reportDrawIds);
        setExistingHiddenCoLienDrawIds(reportHiddenCoLienDrawIds);
        setExistingReportId(magicReportId);
        setMode(selectionMode);
        setDialogOpen(true);
      } catch (error) {
        handleError(error);
      } finally {
        setIsPreparing(false);
      }
    },
    [getConfirmation],
  );

  /**
   * Resolves the report for the chosen draws, loads each draw's budget and line items, and
   * navigates into the wizard.
   */
  const confirmDrawSelection = useCallback(
    async (drawIds: string[], hiddenCoLienDrawIds: string[] = [], options?: { onConfirmReplace?: () => Promise<boolean> }) => {
      if (!project || drawIds.length === 0) return;
      setIsOpening(true);
      try {
        const projectSummary = await getProjectSummary(project.id);
        if (!projectSummary) {
          toast.error('Project not found.');
          return;
        }

        const requiredGap = getMissingRequiredFields(projectSummary);
        if (requiredGap.length > 0) {
          setRequiredFieldsGap(requiredGap);
          setRequiredFieldsGapProjectId(project.id);
          setRequiredFieldsGapProjectSummary(projectSummary);
          setPendingRetry(() => () => confirmDrawSelection(drawIds, hiddenCoLienDrawIds, options));
          closeDialog();
          return;
        }

        const drawName = draws.find(d => d.id === drawIds[drawIds.length - 1])?.name || 'Draw';
        const reportName = `Magic Report: ${project.projectName} - ${drawName}`;

        let report: any;
        if (!existingReportId) {
          report = await createMagicReport({
            name: reportName,
            projectId: projectSummary.projectId,
            drawIds,
            hiddenCoLienDrawIds,
            startsNewPeriod: true,
          });
        } else if (!drawSelectionReplacesReport(drawIds, existingDrawIds, true)) {
          // Draw set effectively unchanged: just reopen. Calling create here would delegate to
          // reset on the backend and destroy the report the user was coming back to.
          const hiddenChanged =
            hiddenCoLienDrawIds.length !== existingHiddenCoLienDrawIds.length ||
            hiddenCoLienDrawIds.some(id => !existingHiddenCoLienDrawIds.includes(id));
          if (hiddenChanged) {
            await updateHiddenCoLienDrawIds(existingReportId, hiddenCoLienDrawIds);
          }
          report = await getMagicReport(existingReportId);
        } else {
          const confirmed = options?.onConfirmReplace ? await options.onConfirmReplace() : true;
          if (!confirmed) return;
          report = await resetMagicReport(existingReportId, projectSummary.projectId, reportName, drawIds, hiddenCoLienDrawIds);
        }

        if (!report?.id) {
          toast.error('Failed to open report.');
          return;
        }

        // Load every covered draw in parallel, keeping the order the dialog produced.
        const drawData = await Promise.all(drawIds.map(drawId => getBudgetandLineItems(project.id, drawId)));
        const drawContexts: DrawContext[] = drawData
          .filter(data => data?.projectDrawSummary)
          .map(data => ({
            drawId: data.projectDrawSummary.id,
            drawSummary: data.projectDrawSummary,
            budgetSummary: data.budgetSummary,
            lineItems: data.lineItems,
            subtotals: data.subtotals,
          }));

        if (drawContexts.length === 0) {
          toast.error('Could not load budget data for the selected draws.');
          return;
        }

        // The primary draw (last = latest application) still drives the sections that have not
        // been migrated to multi-draw, so mirror it into the legacy single-draw fields.
        const primaryDraw = drawContexts[drawContexts.length - 1];

        const reportData = {
          report,
          projectSummary,
          draws: drawContexts,
          drawSummary: primaryDraw.drawSummary,
          budgetSummary: primaryDraw.budgetSummary,
          lineItems: primaryDraw.lineItems,
          summary: { coverPageContent: report?.summary?.coverLetter },
        };

        // First open of a new period (after Start Next and a payapp): offer Run this Month once.
        // Either answer clears the prompt; if clearing fails it is simply offered again next time.
        let runThisMonth = false;
        if (projectSummary.magicReportRunThisMonthPending) {
          runThisMonth = await getConfirmation({
            heading: 'Run this Month',
            description: `This is the first time this report is opened since Start Next Report. Update these sections with this month's payapp?
              <ul>
                <li><b>Reallocations</b></li>
                <li><b>Change Orders</b></li>
                <li><b>Stored Materials</b></li>
                <li><b>Retainage</b></li>
                <li><b>Minor Adjustments</b></li>
              </ul>`,
            confirmButton: { label: 'Run this Month', color: 'primary' },
            cancelButton: { label: 'Not now', color: 'secondary' },
          });
          try {
            await clearRunThisMonthPending(report.id);
          } catch (error) {
            console.error('Failed to clear the Run this Month prompt', error);
          }
        }

        // Run this Month needs the Payapp Review sections mounted, so open straight onto that step.
        const step = runThisMonth ? 'payapp-review' : 'upload-images';
        navigate(`${ROUTES.PROJECTS}/${projectSummary.projectId}${ROUTES.MAGIC_REPORT}/${report.id}/${step}`, {
          state: { reportData, runThisMonth },
        });
        closeDialog();
      } catch (error) {
        handleError(error);
      } finally {
        setIsOpening(false);
      }
    },
    [project, draws, existingReportId, existingDrawIds, existingHiddenCoLienDrawIds, navigate, closeDialog, getConfirmation],
  );

  const retryAfterFieldsFixed = useCallback(() => {
    const retry = pendingRetry;
    closeRequiredFieldsGap();
    retry?.();
  }, [pendingRetry, closeRequiredFieldsGap]);

  return {
    openDrawSelection,
    confirmDrawSelection,
    closeDialog,
    dialogOpen,
    draws,
    project,
    existingDrawIds,
    existingHiddenCoLienDrawIds,
    hasExistingReport: Boolean(existingReportId),
    isPreparing,
    isOpening,
    requiredFieldsGap,
    requiredFieldsGapProjectId,
    requiredFieldsGapProjectSummary,
    requiredFieldsGapOpen: requiredFieldsGap.length > 0,
    retryAfterFieldsFixed,
    closeRequiredFieldsGap,
    mode,
  };
}
